import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, CreditCard, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Faz 2A — Past-Due (ödeme başarısız) durumundaki kullanıcı için
 * hızlı yeniden tahsilat paneli.
 *
 * Kullanıcıya 3 seçenek sunar:
 *   1. Mevcut varsayılan kartla tekrar dene
 *   2. Kayıtlı farklı bir kartla öde (dropdown)
 *   3. Yeni kart ekle (mevcut abonelik iFrame akışına yönlendir)
 */
export default function PastDueRetryCard({ subscription, onRefresh }) {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payment-methods");
        setMethods(data || []);
        const def = (data || []).find((m) => m.is_default);
        setSelectedId(def?.id || (data || [])[0]?.id || "");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const retry = async (pmId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/retry-charge",
        pmId ? { payment_method_id: pmId } : {},
      );
      if (data.ok) {
        toast.success(data.message || "Ödeme başarılı, aboneliğiniz aktif.");
        onRefresh?.();
        // Sayfayı yenile ki context güncellensin
        setTimeout(() => window.location.reload(), 800);
      } else if (data.dry_run) {
        toast.info("Dry-run modu aktif; gerçek tahsilat yapılmadı. Lütfen daha sonra tekrar deneyin.");
      } else {
        toast.error(data.message || "Ödeme başarısız. Lütfen farklı kart deneyin.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ödeme denemesi başarısız");
    } finally {
      setBusy(false);
    }
  };

  const graceDaysLeft = subscription?.grace_days_left;

  return (
    <SectionCard
      title="Ödeme Alınamadı"
      className="border-amber-300 bg-amber-50/40"
    >
      <div className="p-5 space-y-4" data-testid="past-due-retry-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5" />
          </div>
          <div className="flex-1 text-sm text-slate-800 leading-relaxed">
            <div className="font-medium text-slate-900">
              Son abonelik yenilemesi başarısız oldu.
            </div>
            <div className="text-slate-600 mt-1">
              {graceDaysLeft != null && graceDaysLeft > 0 ? (
                <>
                  Hesabınız <b>{graceDaysLeft} gün</b> daha aktif kalacak.
                  Bu sürede tahsilat başarılı olmazsa hesabınız askıya
                  alınır.
                </>
              ) : (
                <>Aboneliğinizi devam ettirmek için lütfen ödeme yönteminizi güncelleyin.</>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Kayıtlı kartlar yükleniyor...
          </div>
        ) : (methods ?? []).length === 0 ? (
          <div className="space-y-3">
            <div className="text-sm text-slate-600 py-3 px-4 bg-white border border-slate-200 rounded-lg">
              Henüz kayıtlı bir kartınız yok. Yeni kart ekleyerek ödemenizi tamamlayın.
            </div>
            <Button
              className="w-full"
              onClick={() => (window.location.href = "/abonelik?add-card=1")}
              data-testid="past-due-add-card"
            >
              <Plus className="w-4 h-4 mr-1" /> Yeni Kart Ekle
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {(methods ?? []).length > 1 && (
              <div className="space-y-1.5">
                <div className="text-xs text-slate-600">Kart seçin</div>
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white"
                  data-testid="past-due-card-select"
                >
                  {(methods ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.display}{m.is_default ? " (Varsayılan)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                disabled={busy || !selectedId}
                onClick={() => retry(selectedId)}
                data-testid="past-due-retry-selected"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />
                {methods.length > 1 ? "Seçili Kartla Öde" : "Mevcut Kartla Tekrar Dene"}
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/abonelik?add-card=1")}
                data-testid="past-due-add-different-card"
              >
                <CreditCard className="w-4 h-4 mr-1" /> Farklı Kart / Yeni Kart Ekle
              </Button>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}
